import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { TextInput } from '../../../../../../../ui/input/text_input/text_input';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { PreQualQuestionType } from '../interfaces';

interface QuestionEditFormProps {
  editQuestionText: string;
  setEditQuestionText: (text: string) => void;
  editQuestionType: PreQualQuestionType;
  setEditQuestionType: (type: PreQualQuestionType) => void;
  questionTypeOptions: SelectOptions[];
  editChoices: string[];
  setEditChoices: React.Dispatch<React.SetStateAction<string[]>>;
  onChoiceChange: (index: number, value: string, edit: boolean) => void;
  onSaveQuestion: () => Promise<void>;
  onCancelEdit: () => void;
}

export const QuestionEditForm = ({
  editQuestionText,
  setEditQuestionText,
  editQuestionType,
  setEditQuestionType,
  questionTypeOptions,
  editChoices,
  setEditChoices,
  onChoiceChange,
  onSaveQuestion,
  onCancelEdit,
}: QuestionEditFormProps) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Grid: Question Text and Type */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' },
          gap: 2,
          alignItems: 'end',
        }}
      >
        <Box>
          <TextInput
            label={t(
              ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.editQuestion,
            )}
            name="editQuestion"
            value={editQuestionText}
            type="text"
            onChange={(e) => setEditQuestionText(e.target.value)}
          />
        </Box>

        <Box>
          <CustomSelect
            label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.type)}
            name="editQuestionType"
            value={questionTypeOptions.find(
              (option) => option.value === editQuestionType,
            )}
            options={questionTypeOptions}
            onChange={(selectedOption: SelectOptions) =>
              setEditQuestionType(selectedOption.value as PreQualQuestionType)
            }
          />
        </Box>
      </Box>

      {/* Multiple Choice Options Builder */}
      {editQuestionType === 'multiple-choice' && (
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            bgcolor: 'slate.50',
            border: '1px solid',
            borderColor: 'slate.200',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {editChoices.map((choice, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box sx={{ width: 170 }}>
                <TextInput
                  name={`edit-choice-${index}`}
                  value={choice}
                  type="text"
                  sx={{ height: 32 }}
                  onChange={(e) => onChoiceChange(index, e.target.value, true)}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  setEditChoices(editChoices.filter((_, i) => i !== index))
                }
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                }}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}

          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditChoices([...editChoices, ''])}
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              height: 32,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.addChoice)}
          </Button>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancelEdit}
          startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 36,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.cancel)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSaveQuestion}
          disabled={editQuestionText.trim() === ''}
          startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 36,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.update)}
        </Button>
      </Box>
    </Box>
  );
};
